// src/hooks/usePageTracking.ts
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pushPageView } from '@/lib/gtmSnippet';
import analyticsConfig from '@/config/gtmConfig';

/**
 * Transform service tab URLs into cleaner virtual paths
 * @param path Original path with query parameters
 * @returns Transformed path for analytics
 */
const getVirtualPath = (path: string): string => {
  // Check if this is a services page with tab parameter
  if (path.startsWith('/services') && path.includes('?tab=')) {
    const tabParam = new URLSearchParams(path.split('?')[1]).get('tab');
    if (tabParam) {
      return `/services/${tabParam}`;
    }
  }
  return path;
};

/**
 * Custom hook to track page views with built-in duplicate prevention
 */
export const usePageTracking = (): void => {
  const location = useLocation();
  const previousPath = useRef<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTrackingEnabled = analyticsConfig.isEnabled;

  // Flag to track if we're handling the initial pageview
  const isFirstPageview = useRef(true);

  useEffect(() => {
    // Don't track if analytics is disabled
    if (!isTrackingEnabled) return;

    const currentPath = location.pathname + location.search;

    // Clear any existing timeouts to avoid race conditions
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only track if path has changed or it's the first pageview
    if (currentPath !== previousPath.current || isFirstPageview.current) {
      previousPath.current = currentPath;

      // Wait a bit to ensure GTM is initialized and prevent duplicates
      timeoutRef.current = setTimeout(() => {
        const title = document.title;

        // Transform the path for cleaner reporting
        const virtualPath = getVirtualPath(currentPath);

        // Construct a clean virtual URL as well
        const virtualUrl = `${window.location.origin}${virtualPath}`;

        // Get the referrer
        const referrer = document.referrer || '';

        // Track the pageview with the transformed path and URL
        pushPageView(virtualPath, title, virtualUrl, referrer);

        // No longer the first pageview
        isFirstPageview.current = false;
      }, 100);
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location, isTrackingEnabled]);
};

export default usePageTracking;
